// ADR-0142 — el reloj del cierre por falta de respuesta. Funciones puras:
// el job programado de core-api las usa para decidir qué recordatorio
// enviar y qué observación cerrar, y los read models para proyectar
// `autoApprovalAt` / `reopenableUntil` en el wire. Una sola fuente, así
// la fecha que promete la pantalla, la del correo y la del cierre real
// son la misma.
//
// Todo se cuenta en DÍAS CORRIDOS de America/Santiago, nunca en
// milisegundos: sumar N×24 h cruza el cambio de hora chileno con una hora
// de más o de menos y, con un ancla cerca de la medianoche, corre la
// fecha un día.
//
// Las reglas (decisiones del fundador, 2026-10-16):
// - el ancla efectiva es la más tardía entre la entrada en `fromStatus` y
//   la activación del job en el entorno: nadie recibe correos seguidos
//   al encenderlo;
// - cada recordatorio vence en max(ancla + díaᵢ, envío real del anterior
//   + (díaᵢ − díaᵢ₋₁)), y el cierre en max(ancla + autoApproveDays,
//   envío real del último + (autoApproveDays − díaₙ)). Un aviso que salió
//   tarde corre todo lo que viene después; uno que no salió bloquea el
//   cierre.
/** La zona del negocio: los plazos son días calendario de Chile. */
export const CLOSURE_TIME_ZONE = "America/Santiago";
/**
 * El reloj de una receta, o null si no cierra sola. Exige las tres
 * claves juntas (el superRefine de la receta también lo exige).
 */
export function closureClockOf(recipe) {
    const c = recipe.observationClosure;
    if (c?.autoApproveDays === undefined ||
        c.reminderDays === undefined ||
        c.reopenWindowDays === undefined) {
        return null;
    }
    return {
        reminderDays: c.reminderDays,
        autoApproveDays: c.autoApproveDays,
        reopenWindowDays: c.reopenWindowDays,
    };
}
const DAY_FORMAT = new Intl.DateTimeFormat("en-CA", {
    timeZone: CLOSURE_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
});
/** «2026-10-16» — el día calendario de un instante, en Santiago. */
export function santiagoDate(instant) {
    return DAY_FORMAT.format(instant);
}
function parseDate(date) {
    const [y, m, d] = date.split("-").map(Number);
    return [y, m, d];
}
/** Suma días calendario a un «AAAA-MM-DD» (sin zona: es aritmética de
 *  calendario pura). */
export function addCalendarDays(date, days) {
    const [y, m, d] = parseDate(date);
    return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}
/** Suma meses de calendario a «AAAA-MM-DD»; un día que no existe en el
 *  mes de llegada cae al último (31-ene + 1 mes = 28/29-feb). Vive aquí
 *  (y no en api-types) desde ADR-0153: el tick de encuestas lo usa en el
 *  servidor y la vigencia del contacto (ADR-0150) lo reexporta. */
export function addCalendarMonths(date, months) {
    const [y, m, d] = parseDate(date);
    const lastDay = new Date(Date.UTC(y, m - 1 + months + 1, 0)).getUTCDate();
    return new Date(Date.UTC(y, m - 1 + months, Math.min(d, lastDay))).toISOString().slice(0, 10);
}
function maxDate(a, b) {
    // «AAAA-MM-DD» ordena lexicográficamente igual que cronológicamente.
    return a >= b ? a : b;
}
/**
 * El primer instante del día `date` en Santiago. El cambio de hora
 * chileno ocurre a medianoche, así que un día puede empezar a la 01:00:
 * se busca el primer instante cuyo día local es `date`, no se asume
 * 00:00.
 */
export function startOfSantiagoDay(date) {
    const [y, m, d] = parseDate(date);
    // El desfase de Chile continental es −3 o −4: el día local empieza
    // entre las 03:00 y las 05:00 UTC. Se recorre hora a hora desde antes.
    const base = Date.UTC(y, m - 1, d);
    for (let h = 0; h <= 8; h++) {
        const t = base + h * 3_600_000;
        if (santiagoDate(new Date(t)) === date) {
            // La hora en que entra puede no ser exacta (zonas con media hora):
            // se afina hacia atrás de a minuto.
            let s = t;
            while (santiagoDate(new Date(s - 60_000)) === date)
                s -= 60_000;
            return new Date(s);
        }
    }
    throw new Error(`no se pudo resolver el inicio del día ${date} en ${CLOSURE_TIME_ZONE}`);
}
const WALL_CLOCK_FORMAT = new Intl.DateTimeFormat("en-GB", {
    timeZone: CLOSURE_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
});
/**
 * ADR-0161 — el instante en que, en Santiago, el día `date` marca la hora
 * de pared `time` («HH:MM»). Se prueban los dos desfases de Chile
 * continental (−3 y −4) y se queda el instante cuyo día y hora locales
 * calzan; nunca `startOfSantiagoDay + horas` (el día del cambio de hora
 * dura 23 o 25 horas).
 *   - Hora que no existe (el salto de septiembre, a las 00:00): el inicio
 *     del día, la primera hora que sí existe.
 *   - Hora ambigua (abril, se repite la hora de antes de medianoche): el
 *     PRIMER instante.
 */
export function santiagoWallInstant(date, time) {
    const [y, m, d] = parseDate(date);
    const hm = /^(\d{2}):(\d{2})/.exec(time);
    if (!hm)
        throw new Error(`hora inválida: ${time}`);
    const hhmm = `${hm[1]}:${hm[2]}`;
    const local = Date.UTC(y, m - 1, d, Number(hm[1]), Number(hm[2]));
    const matches = [3, 4]
        .map((offsetHours) => new Date(local + offsetHours * 3_600_000))
        .filter((t) => santiagoDate(t) === date && WALL_CLOCK_FORMAT.format(t) === hhmm)
        .sort((a, b) => a.getTime() - b.getTime());
    return matches[0] ?? startOfSantiagoDay(date);
}
/** El último milisegundo del día `date` en Santiago. */
export function endOfSantiagoDay(date) {
    return new Date(startOfSantiagoDay(addCalendarDays(date, 1)).getTime() - 1);
}
/** El ancla efectiva: la más tardía entre la entrada en `fromStatus` y
 *  la activación del job. */
export function closureAnchor(statusAt, activatedAt) {
    return statusAt.getTime() >= activatedAt.getTime() ? statusAt : activatedAt;
}
export function closureSchedule(clock, input) {
    const anchorDate = santiagoDate(closureAnchor(input.statusAt, input.activatedAt));
    const today = santiagoDate(input.now);
    const reminderDueDates = [];
    let prevDate = anchorDate;
    let prevDay = 0;
    let nextReminder = null;
    clock.reminderDays.forEach((day, i) => {
        const due = maxDate(addCalendarDays(anchorDate, day), addCalendarDays(prevDate, day - prevDay));
        reminderDueDates.push(due);
        const sent = input.remindersSentAt[i] ?? null;
        if (sent === null && nextReminder === null)
            nextReminder = i;
        // Un aviso enviado cuenta desde su día real; uno pendiente, desde el
        // día en que vence o desde hoy si ya venció (no salió todavía).
        prevDate = sent !== null ? santiagoDate(sent) : maxDate(due, today);
        prevDay = day;
    });
    const closeDueDate = maxDate(addCalendarDays(anchorDate, clock.autoApproveDays), addCalendarDays(prevDate, clock.autoApproveDays - prevDay));
    return { anchorDate, reminderDueDates, closeDueDate, nextReminder };
}
/** `autoApprovalAt` del wire: el inicio del día de cierre proyectado. */
export function projectedAutoApprovalAt(clock, input) {
    return startOfSantiagoDay(closureSchedule(clock, input).closeDueDate);
}
/** Último instante de la ventana de reapertura de un cierre por falta de
 *  respuesta: el fin del día (Santiago) `cierre + reopenWindowDays`. */
export function reopenWindowEnd(clock, closedAt) {
    return endOfSantiagoDay(addCalendarDays(santiagoDate(closedAt), clock.reopenWindowDays));
}
/** El motivo del overlay que escribe el cierre por falta de respuesta. */
export const AUTO_CLOSURE_REASON = "auto_approval";
/**
 * `reopenableUntil` del wire: el fin de la ventana si la observación está
 * cerrada por falta de respuesta y la ventana sigue vigente; null en
 * cualquier otro caso (cerrada por conformidad, migrada, abierta, o la
 * ventana ya pasó). La ruta de reapertura usa esta misma función.
 */
export function autoClosureReopenableUntil(clock, row, now) {
    if (clock === null)
        return null;
    if (row.observationClosedReason !== AUTO_CLOSURE_REASON || row.observationClosedAt === null) {
        return null;
    }
    const end = reopenWindowEnd(clock, row.observationClosedAt);
    return now.getTime() <= end.getTime() ? end : null;
}
//# sourceMappingURL=observation-closure-clock.js.map